/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

//react imports
import React, { useCallback, useEffect, useMemo, useState } from 'react';

//react native imports
import * as Linking from 'expo-linking';
import { useTranslation } from 'react-i18next';
import {
  Text,
  View,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

//phoenix imports
import { getTextStyle, pd } from '@airasia-phoenix/react-native/utils/utils';
import ContentCardGroupView from '@airasia-phoenix/react-native/widgets/ContentCardGroupView';

import { StationTypeFilter, useStationsMap } from '@airasia-rn/purchases';

//ota imports
import { SCREEN_NAME } from '../../config/analytics';
import { useFlightRecommendations } from '../../hooks/useFlightRecommendations';
import Constants from './constants';
import {
  FlightDealsItemAPIResponse,
  FlightDealsProps,
  FlightsDealsCarouselItemProps,
} from './types';
import { useFlightDealsCarouselHandlers } from './useFlightDealsCarouselHandlers';
import { mapToVerticalContentCardProps } from './utils';

const FlightDealsCarouselView = (props: FlightDealsProps) => {
  const { onPressCard } = props;
  const { stationsMap, isStationsLoading, isStationsError } = useStationsMap(
    StationTypeFilter.AIRPORT,
  );
  const [carouselData, setCarouselData] = useState<
    FlightsDealsCarouselItemProps[]
  >([]);

  const { t } = useTranslation('flights');

  const [isAtStart, setIsAtStart] = useState(true);

  const {
    data: recommendationResponse,
    isLoading,
    isError,
    error,
  } = useFlightRecommendations('FLIGHT', SCREEN_NAME.flightSearchList);

  const items = useMemo(
    () => recommendationResponse?.groups?.[0]?.items ?? [],
    [recommendationResponse],
  );

  const handleDealPress = useCallback(
    (item: FlightDealsItemAPIResponse) => {
      const deepLinkUrl = item.deepLink ?? '';
      const parsed = Linking.parse(deepLinkUrl);
      const queryParams = parsed.queryParams;

      if (queryParams) {
        const departureStationCode = String(
          queryParams.departureStation || '',
        ).toUpperCase();
        const arrivalStationCode = String(
          queryParams.arrivalStation || '',
        ).toUpperCase();

        const originObj = stationsMap[departureStationCode] || {};
        const destinationObj = stationsMap[arrivalStationCode] || {};
        onPressCard?.({
          ...queryParams,
          originObj,
          destinationObj,
        });
      }
    },
    [stationsMap, onPressCard],
  );

  // handlers moved to hook (env-aware)
  const { handleImageError, handleScroll, initCounters } =
    useFlightDealsCarouselHandlers({
      recommendationItems: items,
      setCarouselData,
      onItemPress: () => {},
    });

  const handleCarouselScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetX = event.nativeEvent.contentOffset.x;

      // Detect if fully at start
      setIsAtStart(offsetX <= 0);

      // Keep infinite scroll working
      handleScroll(event);
    },
    [handleScroll], // important dependency
  );

  useEffect(() => {
    if (!items.length) return;

    const mappedData = mapToVerticalContentCardProps(
      items,
      handleImageError,
      handleDealPress,
    );

    setCarouselData(mappedData);
    initCounters(items.length);
  }, [items.length, handleImageError, handleDealPress, initCounters]);

  const contentContainerStyle = useMemo(
    () => ({
      paddingLeft: isAtStart ? 7 : 0,
    }),
    [isAtStart],
  );

  if (isLoading) return null;

  if (isError) {
    console.error(error);
    return null;
  }

  if (!carouselData.length) return null;

  return (
    <View style={styles.container}>
      <Text
        style={[
          getTextStyle(
            pd.sys.typescale.body_text_medium,
            pd.sys.colour.content_primary,
          ),
          styles.label,
        ]}
      >
        {t('flightsSearchPage.main.flightDealsLabel')}
      </Text>

      <View>
        <ContentCardGroupView
          orientation={Constants.CAROUSEL_ORIENTATION}
          contentCardProps={carouselData}
          viewHeight={Constants.CAROUSEL_HEIGHT}
          //@ts-expect-error renderItem already passed.
          flatListProps={{
            decelerationRate: Constants.SCROLL_SPEED,
            showsHorizontalScrollIndicator: false,
            onScroll: handleCarouselScroll,
            scrollEventThrottle: 16,
            contentContainerStyle: contentContainerStyle,
          }}
        />
      </View>
    </View>
  );
};

export default FlightDealsCarouselView;

const styles = StyleSheet.create({
  container: {
    marginTop: 16,
  },
  label: {
    marginBottom: 10,
    left: 16,
  },
});
